import React, { useEffect, useRef, useState } from "react";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import MetaTags from "react-meta-tags";
import RegisterCTA from "../../components/buttons/RegisterCTA";
import SalesCTA from "../../components/buttons/SalesCTA";

// Component Wrapper untuk animasi slide-in saat scroll
function AnimatedFeatureRow({ feature }) {
  const rowRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      if (rowRef.current) observer.unobserve(rowRef.current);
    };
  }, []);

  const textAnimationClass = isVisible
    ? feature.reverse
      ? "slide-right-active"
      : "slide-left-active"
    : "";

  const mediaAnimationClass = isVisible
    ? feature.reverse
      ? "slide-left-active"
      : "slide-right-active"
    : "";

  return (
    <div ref={rowRef} style={{ overflow: "hidden", padding: "10px 0" }}>
      <Grid
        container
        spacing={6}
        alignItems="center"
        justify="space-between"
        direction={feature.reverse ? "row-reverse" : "row"}
      >
        {/* Text Col */}
        <Grid
          item
          xs={12}
          md={6}
          className={textAnimationClass}
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: feature.kickerColor || "#0284c7",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            {feature.kicker}
          </div>
          <Typography
            variant="h3"
            style={{
              fontWeight: 800,
              color: "#0f172a",
              fontSize: "clamp(1.6rem, 2.5vw, 2.1rem)",
              lineHeight: 1.25,
              marginBottom: "14px",
            }}
          >
            {feature.title}
          </Typography>
          <Typography
            variant="body1"
            style={{
              color: "#64748b",
              fontSize: "15px",
              lineHeight: 1.7,
            }}
          >
            {feature.desc}
          </Typography>
        </Grid>

        {/* Media Col */}
        <Grid
          item
          xs={12}
          md={6}
          className={mediaAnimationClass}
          style={{
            textAlign: "center",
            opacity: isVisible ? 1 : 0,
          }}
        >
          {feature.isVideo ? (
            <video
              style={{
                width: "100%",
                height: "auto",
                borderRadius: "12px",
                display: "block",
              }}
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={feature.mediaSrc} type="video/mp4" />
            </video>
          ) : (
            <img
              src={feature.mediaSrc}
              alt={feature.title}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "420px",
                objectFit: "contain",
                borderRadius: "12px",
                display: "inline-block",
              }}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

export default function Partner() {
  const partnerReasons = [
    {
      title: "Multi Platform & Web Backoffice",
      desc: "Kelola operasional harian lewat aplikasi tablet/ponsel kasir, dan atur strategi bisnis menyeluruh via Web Backoffice.",
      icon: "./asset/images/icons/platform.webp",
    },
    {
      title: "Manajemen Menu & Stok Digital",
      desc: "Tambah hidangan, atur ketersediaan stok bahan, ubah harga, dan buat varian diskon dengan mudah.",
      icon: "./asset/images/icons/digital-menu.webp",
    },
    {
      title: "Service Calling System",
      desc: "Pelanggan dapat memanggil staf atau meminta tagihan langsung dari meja. Pelayan merespons lebih cepat.",
      icon: "./asset/images/icons/service-calling.webp",
    },
    {
      title: "Multi Digital Payment",
      desc: "Terima pembayaran tunai, kartu debit/kredit, hingga seluruh QRIS dan e-Wallet dalam rekonsiliasi satu pintu.",
      icon: "./asset/images/icons/digital-payment.webp",
    },
    {
      title: "Laporan & Analisis Real-Time",
      desc: "Laporan penjualan harian, tren jam ramai, dan margin keuntungan dapat dipantau langsung dari gadget Anda.",
      icon: "./asset/images/icons/report-analysis.webp",
    },
    {
      title: "Peningkatan Efisiensi Kasir",
      desc: "Kurangi kesalahan input pesanan dan tingkatkan efisiensi perputaran meja di setiap shift kerja restoran.",
      icon: "./asset/images/icons/incrase-sales.webp",
    },
  ];

  const featureDeepDives = [
    {
      kicker: "ORDER PROCESSING",
      kickerColor: "#0284c7",
      title: "Manajemen Pesanan Masuk Terstruktur",
      desc: "Setiap pesanan dari meja langsung masuk ke display kitchen dan kasir secara teratur dengan status jelas (Baru, Dimasak, Siap Saji, Selesai).",
      mediaSrc: "./asset/images/features/fitur-terima-order-new.png",
      isVideo: false,
      reverse: false, // Text Kiri, Image Kanan
    },
    {
      kicker: "TRANSACTION HISTORY",
      kickerColor: "#DE148C",
      title: "Riwayat Transaksi & Audit Kasir",
      desc: "Lacak kembali setiap order per meja, jam transaksi, metode pembayaran yang digunakan, hingga detail diskon yang diterapkan secara akurat.",
      mediaSrc: "./asset/images/features/order-history-new.png",
      isVideo: false,
      reverse: true, // Image Kiri, Text Kanan
    },
    {
      kicker: "PRINTER INTEGRATION",
      kickerColor: "#0284c7",
      title: "Cetak Struk Instan via Bluetooth",
      desc: "Kompatibel dengan printer thermal bluetooth maupun jaringan kabel tanpa memerlukan instalasi driver tambahan yang rumit.",
      mediaSrc: "./asset/images/features/fitur-cetak-new.mp4",
      isVideo: true,
      reverse: false, // Text Kiri, Video Kanan
    },
  ];

  const onboardingSteps = [
    {
      step: "01",
      title: "Daftar Akun Merchant",
      desc: "Isi data profil usaha Anda secara online dalam waktu 3 menit.",
    },
    {
      step: "02",
      title: "Setup Menu & Meja",
      desc: "Input daftar menu dan nomor meja restoran Anda dengan panduan tim support kami.",
    },
    {
      step: "03",
      title: "Langsung Siap Pakai",
      desc: "Cetak barcode QR meja atau mulai gunakan aplikasi kasir POS untuk melayani pelanggan.",
    },
  ];

  return (
    <div style={{ maxWidth: "100%", overflowX: "hidden", backgroundColor: "#ffffff" }}>
      <MetaTags>
        <title>UR Partner : Solusi POS &amp; Manajemen Restoran Modern</title>
        <meta
          name="title"
          content="UR Partner : Solusi POS & Manajemen Restoran Modern"
        />
        <meta
          name="description"
          content="UR Partner membantu mengolah bisnis Anda seperti menu digital, payment gateway, laporan penjualan, dan efisiensi staf."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ur-hub.com/partner" />
      </MetaTags>

      {/* Hero Section */}
      <section
        style={{
          paddingTop: "130px",
          paddingBottom: "72px",
          backgroundColor: "#0f172a",
          color: "#ffffff",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" justify="space-between">
            <Grid item xs={12} md={7}>
              <div
                style={{
                  color: "#38bdf8",
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "12px",
                }}
              >
                Sistem Merchant &amp; POS Kasir
              </div>

              <Typography
                variant="h1"
                style={{
                  fontWeight: 800,
                  fontSize: "clamp(2rem, 4vw, 3.2rem)",
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  marginBottom: "16px",
                }}
              >
                Tingkatkan Produktivitas Restoran Anda dengan UR Partner
              </Typography>

              <Typography
                variant="subtitle1"
                style={{
                  color: "#94a3b8",
                  fontSize: "15px",
                  lineHeight: 1.7,
                  marginBottom: "32px",
                  maxWidth: "560px",
                }}
              >
                Sistem Point-of-Sale dan manajemen pesanan yang dirancang khusus untuk mempermudah operasional cafe, resto, dan merchant kuliner di Indonesia.
              </Typography>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <RegisterCTA
                  text="Coba Gratis UR Partner"
                  padding="12px 26px"
                  fontSize={15}
                />
                <SalesCTA
                  text="Jadwalkan Demo"
                  padding="12px 22px"
                  fontSize={15}
                />
              </div>
            </Grid>

            {/* Right Logo Card */}
            <Grid item xs={12} md={5} style={{ textAlign: "center" }}>
              <div
                style={{
                  padding: "36px",
                  borderRadius: "20px",
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "inline-block",
                }}
              >
                <img
                  src="./asset/images/Logo/icon-ur-magenta-new.png"
                  alt="UR Partner Logo"
                  style={{
                    maxHeight: "180px",
                    width: "auto",
                  }}
                />
                <div style={{ marginTop: "20px" }}>
                  <div
                    style={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "18px",
                      marginBottom: "4px",
                    }}
                  >
                    UR Partner Backoffice
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: "13px" }}>
                    Semua kontrol bisnis dalam satu dashboard
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* Why Choose UR Partner Grid */}
      <section style={{ padding: "80px 0", backgroundColor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <Typography
              variant="h2"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
                letterSpacing: "-0.02em",
                marginBottom: "10px",
              }}
            >
              Mengapa Memilih UR Partner?
            </Typography>
            <p
              style={{
                color: "#64748b",
                fontSize: "15px",
                maxWidth: "560px",
                margin: "0 auto",
              }}
            >
              Dirancang untuk mengatasi kendala antrian meja kasir, akurasi stok resep, dan transparansi laporan penjualan.
            </p>
          </div>

          <Grid container spacing={3}>
            {partnerReasons.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "14px",
                    padding: "24px",
                    height: "100%",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.2s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.04)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "10px",
                      backgroundColor: "#f0f9ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.title}
                      style={{ width: "28px", height: "28px", objectFit: "contain" }}
                    />
                  </div>

                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}
                  >
                    {item.desc}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Feature Deep Dive dengan Slide-In Animation */}
      <section style={{ padding: "80px 0", backgroundColor: "#ffffff" }}>
        <Container maxWidth="lg">
          <div style={{ display: "flex", flexDirection: "column", gap: "72px" }}>
            {featureDeepDives.map((feature, idx) => (
              <AnimatedFeatureRow key={idx} feature={feature} />
            ))}
          </div>
        </Container>
      </section>

      {/* 3-Step Onboarding */}
      <section
        style={{
          padding: "72px 0",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Container maxWidth="lg">
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <Typography
              variant="h3"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                fontSize: "24px",
                marginBottom: "8px",
              }}
            >
              Mulai dalam 3 Langkah Mudah
            </Typography>
            <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
              Outlet Anda siap menerima pesanan dalam waktu singkat.
            </p>
          </div>

          <Grid container spacing={3} justify="center">
            {onboardingSteps.map((item, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "14px",
                    padding: "28px 24px",
                    height: "100%",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: 800,
                      color: "#94a3b8",
                      lineHeight: 1,
                      marginBottom: "14px",
                    }}
                  >
                    {item.step}
                  </div>
                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "#0f172a",
                      marginBottom: "8px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}
                  >
                    {item.desc}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section
        style={{
          padding: "72px 0",
          backgroundColor: "#0f172a",
          color: "#ffffff",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            style={{
              fontWeight: 800,
              fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
              marginBottom: "14px",
            }}
          >
            Siap Bergabung Bersama UR Partner?
          </Typography>
          <p
            style={{
              color: "#94a3b8",
              fontSize: "15px",
              maxWidth: "500px",
              margin: "0 auto 32px auto",
            }}
          >
            Rasakan kemudahan mengelola transaksi restoran, split bill, dan laporan bisnis dalam satu aplikasi kasir.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <RegisterCTA text="Daftar Partner Gratis" />
            <SalesCTA text="Hubungi Konsultan Kami" />
          </div>
        </Container>
      </section>
    </div>
  );
}